import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { versionLabel, type MyPolicyDetail, type PolicyAcceptanceStatus } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingPane,
  PageHeader,
  formatDate,
  formatDateTime,
  useToast,
} from '../../components/ui.js';
import { Markdown } from '../../components/Markdown.js';
import { BackToTop, DocumentOutline, hasOutline } from '../corpus/DocumentOutline.js';
import { STATUS_META, policiesKey, reasonLabel, releaseKey } from './shared.js';
import '../corpus/corpus.css';
import './policies.css';

/**
 * Read the released text of a policy and accept it. Content is the version
 * that was published (never the item's current draft); the checkbox carries
 * the exact statement the server records. No scroll-to-bottom gating — it
 * proves nothing and hurts keyboard and screen-reader users.
 */
export function PolicyReaderPage() {
  const { releaseId = '' } = useParams();
  const { org } = useOrg();
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const impersonating = me?.impersonating != null;

  const detail = useQuery({
    queryKey: releaseKey(org.orgId, releaseId),
    queryFn: () => apiFetch<MyPolicyDetail>(`/orgs/${org.orgId}/my-policies/${releaseId}`),
    enabled: releaseId !== '',
  });

  const [ticked, setTicked] = useState(false);
  useEffect(() => setTicked(false), [releaseId]);

  const showOutline = useMemo(
    () => (detail.data ? hasOutline(detail.data.contentMarkdown) : false),
    [detail.data?.contentMarkdown],
  );

  const accept = useMutation({
    mutationFn: () =>
      apiFetch<PolicyAcceptanceStatus>(`/orgs/${org.orgId}/my-policies/${releaseId}/accept`, {
        method: 'POST',
        body: { confirm: true },
      }),
    onSuccess: () => {
      toast('Thank you — your acceptance has been recorded.', 'success');
      void queryClient.invalidateQueries({ queryKey: policiesKey(org.orgId) });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.details?.reason === 'release_superseded') {
        const current = err.details.currentReleaseId;
        toast('A newer version has been published — please read and accept that one.', 'error');
        void queryClient.invalidateQueries({ queryKey: policiesKey(org.orgId) });
        if (typeof current === 'string') navigate(`/policies/${current}`, { replace: true });
        return;
      }
      if (err instanceof ApiRequestError && err.details?.reason === 'already_accepted') {
        void queryClient.invalidateQueries({ queryKey: policiesKey(org.orgId) });
        return;
      }
      toast(err instanceof ApiRequestError ? err.message : 'Could not record your acceptance', 'error');
    },
  });

  if (detail.isLoading) return <LoadingPane label="Loading policy" />;
  if (detail.isError || !detail.data) {
    return (
      <div>
        <p>
          <Link to="/policies">← My policies</Link>
        </p>
        <EmptyState
          title="Policy not found"
          message="This link does not point at a policy published in your organisation."
        />
      </div>
    );
  }
  const { release, contentMarkdown, statement, status, history } = detail.data;
  const meta = status ? STATUS_META[status.status] : null;
  const sub = status ? reasonLabel(status.reason, release.reacceptIntervalMonths) : null;
  const releaseVersion = versionLabel(release.documentVersion, release.versionNumber);
  const acceptedVersion =
    status?.acceptedVersion != null ? versionLabel(status.acceptedDocumentVersion ?? '', status.acceptedVersion) : null;

  return (
    <div>
      <p>
        <Link to="/policies">← My policies</Link>
      </p>
      <PageHeader
        title={release.title}
        subtitle={
          <>
            {releaseVersion} · published {formatDate(release.publishedAt)}
            {status && status.status !== 'accepted' && <> · due {formatDate(status.dueOn)}</>}
            {meta && (
              <>
                {' '}
                <Badge tone={meta.tone}>{meta.label}</Badge>
                {sub && <> · {sub}</>}
              </>
            )}
          </>
        }
      />

      {!release.live && (
        <div className="policy-notice policy-notice-warning" role="status">
          {release.currentReleaseId ? (
            <>
              <p>This version is no longer current — a newer version has been published.</p>
              <Link to={`/policies/${release.currentReleaseId}`}>Open the current version →</Link>
            </>
          ) : (
            <p>This policy has been withdrawn from acceptance. Nothing more is needed from you.</p>
          )}
        </div>
      )}

      {status?.reason === 'new_version' && release.releaseNotes && (
        <aside className="policy-changes" aria-label="What's changed">
          <h3>What&rsquo;s changed</h3>
          <p>{release.releaseNotes}</p>
        </aside>
      )}

      <Card title="Policy">
        {contentMarkdown ? (
          <div className={showOutline ? 'corpus-doc' : undefined}>
            {showOutline && <DocumentOutline markdown={contentMarkdown} />}
            <div className="corpus-doc-content">
              <Markdown markdown={contentMarkdown} />
              <BackToTop markdown={contentMarkdown} />
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--color-text-muted)' }}>This version has no content.</p>
        )}
      </Card>

      {status && (
        <Card title="Your acceptance" className="policy-accept">
          {status.status === 'accepted' ? (
            <>
              <div className="policy-accepted" role="status">
                <span aria-hidden="true">✓</span>
                <span>
                  You accepted {acceptedVersion} on {formatDate(status.acceptedAt)}
                  {status.method === 'recorded' ? ' (recorded by an admin)' : ''}.
                  {status.renewBy && <> Reconfirm by {formatDate(status.renewBy)}.</>}
                </span>
              </div>
              {status.acceptedVersion !== null && status.acceptedVersion < release.versionNumber && (
                <p className="policy-history">
                  {releaseVersion} was published as a minor change, so your acceptance of {acceptedVersion}{' '}
                  still stands.
                </p>
              )}
            </>
          ) : (
            <>
              {impersonating && (
                <div className="policy-notice policy-notice-danger" role="alert">
                  <p>Only the person themselves can accept a policy — not while viewing as them.</p>
                </div>
              )}
              <label className="policy-accept-statement">
                <input
                  type="checkbox"
                  checked={ticked}
                  disabled={impersonating || accept.isPending}
                  onChange={(e) => setTicked(e.target.checked)}
                />
                <span>{statement}</span>
              </label>
              <div className="policy-accept-actions">
                <Button
                  disabled={!ticked || impersonating}
                  loading={accept.isPending}
                  onClick={() => accept.mutate()}
                >
                  Accept
                </Button>
                {status.dueOn && (
                  <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                    {status.status === 'overdue' ? 'Was due' : 'Due'} {formatDate(status.dueOn)}
                  </span>
                )}
              </div>
            </>
          )}
          {history.length > 0 && (
            <div className="policy-history">
              Your acceptance history for this policy:
              <ul>
                {history.map((h) => (
                  <li key={h.id}>
                    {versionLabel(h.documentVersion, h.versionNumber)} · {formatDateTime(h.acceptedAt)}
                    {h.method === 'recorded' && (
                      <>
                        {' '}
                        · recorded by {h.recordedByName ?? 'an admin'}
                        {h.source ? ` (${h.source})` : ''}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
